import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { NgKLoggerService } from '@kin/ngk-logger';
import { WA_WINDOW } from '@ng-web-apis/common';

import { AppConfigService } from 'app/app-config';
import { PolicyStore } from 'app/home/common/services/legacy/policies/policies.store';
import { QuoteDraftStore } from 'app/home/global-store/quote-draft';
import { UserStore } from 'app/home/global-store/user/user.store';

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

export enum SegmentEvent {
  USER_LOGIN = 'user_login',
  // Home funnel events
  ABOUT_YOU_FORM_SUBMITTED = 'self_serve_about_you_form_submitted',
  CO_APPLICANT_PAGE_MODIFIED = 'self_serve_coapplicant_page_modified',
  CO_APPLICANT_PAGE_SUBMITTED = 'self_serve_coapplicant_page_submitted',
  CO_APPLICANT_PAGE_VIEWED = 'self_serve_coapplicant_page_seen',
  INSURANCE_INFO_PAGE_SUBMITTED = 'self_serve_insurance_info_page_submitted',
  INSURANCE_INFO_PAGE_VIEWED = 'self_serve_insurance_info_page_seen',
  QUOTE_COMPARE_SUBMITTED = 'self_serve_quote_compare_submitted',
  QUOTE_COMPARE_VIEWED = 'self_serve_quote_compare_seen',
  QUOTE_MODIFIED = 'self_serve_quote_modified',
  QUOTE_SUBMITTED = 'self_serve_quote_submitted',
  QUOTE_VIEWED = 'self_serve_quote_seen',
  // Auto funnel events
  AUTO_SELF_SERVE_ADDRESS_ENTERED_SEEN = 'auto_self_serve_address_entered_seen',
  AUTO_SELF_SERVE_ADDRESS_ENTERED_SUBMITTED = 'auto_self_serve_address_entered_submitted',
  AUTO_SELF_SERVE_CUSTOMER_BLOCKED_SEEN = 'auto_self_serve_customer_blocked_seen',
  AUTO_SELF_SERVE_CUSTOMER_REQUESTED_CALL_CLICKED = 'auto_self_serve_customer_requested_call_clicked',
  AUTO_SELF_SERVE_DRIVER_DETAILS_SEEN = 'auto_self_serve_driver_details_seen',
  AUTO_SELF_SERVE_DRIVER_DETAILS_SUBMITTED = 'auto_self_serve_driver_details_submitted',
  AUTO_SELF_SERVE_DRIVERS_CONFIRMED_SEEN = 'auto_self_serve_drivers_confirmed_seen',
  AUTO_SELF_SERVE_DRIVERS_CONFIRMED_SUBMITTED = 'auto_self_serve_drivers_confirmed_submitted',
  AUTO_SELF_SERVE_DRIVERS_HELP_CLICKED = 'auto_self_serve_drivers_help_clicked',
  AUTO_SELF_SERVE_ESTIMATE_QUOTE_IDLE = 'auto_self_serve_estimate_quote_idle',
  AUTO_SELF_SERVE_ESTIMATE_QUOTE_SEEN = 'auto_self_serve_estimate_quote_seen',
  AUTO_SELF_SERVE_FINAL_PRICE_CLICKED = 'auto_self_serve_final_price_clicked',
  AUTO_SELF_SERVE_UW_KNOCKOUT_QUESTIONS_SEEN = 'auto_self_serve_uw_knockout_questions_seen',
  AUTO_SELF_SERVE_UW_KNOCKOUT_QUESTIONS_SUBMITTED = 'auto_self_serve_uw_knockout_questions_submitted',
  AUTO_SELF_SERVE_VEHICLES_CONFIRMED_SEEN = 'auto_self_serve_vehicles_confirmed_seen',
  AUTO_SELF_SERVE_VEHICLES_CONFIRMED_SUBMITTED = 'auto_self_serve_vehicles_confirmed_submitted',
  AUTO_SELF_SERVE_VEHICLES_HELP_CLICKED = 'auto_self_serve_vehicles_help_clicked',
}

// Used to determine product type for Segement events
const AUTO_EVENT_PREFIX = 'auto_self_serve';
const HOME_EVENT_PREFIX = 'self_serve';

// Product types
const AUTO_PRODUCT = 'auto';
const HOME_PRODUCT = 'home';

/**
 * Google Tag Manager Service (GTM Service)
 *   - manages GTM initialization and dataLayer events
 *   - GTM provided source from the container:
 *       https://tagmanager.google.com/#/container/accounts/1491202156/containers/7030390/workspaces/354/tags
 *   - @todo abstract this to something that can be used across apps, see QT-311
 */

@Injectable({
  providedIn: 'root',
})
export class GoogleTagManagerService {
  private gtmContainerId = 'GTM-TH4CKLW'; // This is currently the only GTM container in use
  private window = inject(WA_WINDOW);
  private logger = inject(NgKLoggerService);
  private _appConfig = inject(AppConfigService);
  private userStore = inject(UserStore);
  private policyStore = inject(PolicyStore);
  private quoteDraftStore = inject(QuoteDraftStore);
  private document = this.window.document;
  private isNotProd = true; // Defaults to true, but will be set in init(), appContext is not available when GTM service is instantiated
  private hasGtmScript = false;
  private segmentUserLoaded = false;
  private pendingEvents = signal<Array<{ event: string; payload: Record<string, unknown> }>>([]);

  constructor() {
    // Flush queued sendDataLayerEventWithQuoteData calls once kinPersonId becomes available.
    // For new users, kinPersonId is set by Fullstory AFTER navigation away from the about-you page,
    // so the event must be deferred here in the long-lived service rather than in the component.
    effect(() => {
      if (this.userStore.kinPersonId() !== undefined) {
        untracked(() => {
          const events = this.pendingEvents();
          if (events.length > 0) {
            events.forEach(({ event, payload }) => this.dispatchDataLayerEventWithQuoteData(event, payload));
            this.pendingEvents.set([]);
          }
        });
      }
    });
  }

  /**
   * Init the dataLayer and push the start event
   *   - if GTM fails to load (or actively blocked), define a global
   *       `dataLayer` array to fallback and fail gracefully
   */

  // GTM source:
  //     w[l] = w[l] || [];
  //     w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  private initDataLayer() {
    this.window.dataLayer = this.window.dataLayer || [];
    this.window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
  }

  public init() {
    this.isNotProd = this._appConfig.appContext?.notProd ?? true;

    /**
     * Don't load prod GTM container in non-production environments
     */
    // if (this.isNotProd) {
    //   this.gtmContainerId = 'TESTING';
    // }

    /**
     * Don't load multiple instances of the GTM script
     */
    this.hasGtmScript = !!this.document.getElementById('gtmQuoteContainer');
    if (this.hasGtmScript) {
      return;
    }

    // GTM source:
    //     var f = d.getElementsByTagName(s)[0],
    const firstScriptElement = this.document.getElementsByTagName('script')[0]; // This is how the GTM snippet does it, and if we run into issues we can insert from <head> with something like `headElt.insertBefore(gtmScript, headElt.children[0])`

    // GTM source:
    //       j = d.createElement(s),
    const gtmScript = document.createElement('script');

    // GTM source:
    //     j.async = true;
    gtmScript.async = true;

    // GTM source:
    // (not sure why there is a self comparison here when 'dataLayer' is hardcoded in source)
    //       dl = l != 'dataLayer' ? '&l=' + l : '';
    //     j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmContainerId}&l=dataLayer`;
    gtmScript.id = 'gtmQuoteContainer';

    /**
     * When there is something wrong with DOM script creation, so if NgKLogger is available we should capture it
     */
    if (!firstScriptElement.parentNode) {
      this.logger.error({ message: 'Could not create gtm.js script element because parentNode is missing', context: 'Google Tag Manager Init', priority: 'P4' });
      return;
    }

    /**
     * Google says to, "Paste this code as high in the <head> of the page as possible"
     *   - GTM source uses the first <script> element, and if we run into issues we can load from <head>
     *       with something like `headElt.insertBefore(testScript, headElt.children[0])`
     */
    // GTM source:
    //     f.parentNode.insertBefore(j, f);
    firstScriptElement.parentNode.insertBefore(gtmScript, firstScriptElement);
    this.initDataLayer();
  }

  /**
   * Getter for dataLayer
   */
  public getDataLayer(): unknown[] {
    return this.window.dataLayer;
  }

  /**
   * Setter dataLayer event (appends)
   */
  public sendDataLayerEvent(event: string, payload: Record<string, unknown> = {}) {
    this.pushOnDataLayer({
      event,
      data: {
        kin_segment_user_id: this.userStore.kinSegmentId(),
        kin_id: this.userStore.kinId(),
        kin_person_id: this.userStore.kinPersonId(),
        product: this.getProduct(event),
        variant: 'v1', // So we know the tracking event comes from the new quoting-ui funnel
        ...payload,
      },
    });
  }

  /**
   * Setter for kin_id
   * - this is the user identifier for Segment, id from the users table
   */
  public setKinId(kin_id: number) {
    if (this.userStore.kinId() === kin_id) {
      return;
    }

    // Log event if overwriting kin_id
    if (this.userStore.kinId() !== undefined) {
      this.logger.event({
        name: 'GTMService::SetKinId',
        metaData: {
          message: 'The kin_id was already set, and has been overwritten.',
        },
      });
    }

    this.userStore.setKinId(kin_id);
    this.sendDataLayerUserLoginEvent();
  }

  /**
   * Setter for kin_person_id
   *   - this is the user identifier for Segment, id from the people table
   */
  public setKinPersonId(kin_person_id: number) {
    if (this.userStore.kinPersonId() === kin_person_id) {
      return;
    }
    // Log event if overwriting kin_person_id
    if (this.userStore.kinPersonId() !== undefined) {
      this.logger.event({
        name: 'GTMService::SetKinPersonId',
        metaData: {
          message: 'The kin_person_id was already set, and has been overwritten.',
        },
      });
    }
    this.userStore.setKinPersonId(kin_person_id);
  }

  /**
   * Setter for kin_segment_user_id
   * - this is the user identifier for Segment, composite identifier using id from the people table, "production-xxxxxx" format
   * - in production, the kin_segment_user_id should NOT have the environment prefix
   * - but in non-production it should have the environment prefix
   */
  public setKinSegmentId(kin_segment_user_id: string) {
    if (this.userStore.kinSegmentId() === kin_segment_user_id) {
      return;
    }

    // Log event if overwriting kin_segment_user_id
    if (this.userStore.kinSegmentId() !== '') {
      this.logger.event({
        name: 'GTMService::SetKinSegmentId',
        metaData: {
          message: 'The kin_segment_user_id was already set, and has been overwritten.',
        },
      });
    }

    // Remove the prefix from the kin_segment_user_id if the environment is production
    if (!this.isNotProd) {
      const parts = kin_segment_user_id.split('-');
      if (parts.length > 1) {
        kin_segment_user_id = parts[1];
      }
    }

    this.userStore.setKinSegmentId(kin_segment_user_id);
    this.sendDataLayerUserLoginEvent();
  }

  /**
   * Identifies a customer whose IDs arrived from an upstream handoff (e.g. the Customer Portal deep link)
   * rather than from the quote flow, so funnel events are attributed to the existing customer.
   *   - composes kin_segment_user_id from the current environment; the quote-flow callers instead receive it
   *       pre-formatted from the backend
   *   - setKinSegmentId and setKinId each attempt the user_login event, which only fires once both are set,
   *       so a missing kin_id means no login event rather than a partial one
   */
  public identifyExistingCustomer(kin_person_id: number, kin_id: number) {
    const environment = this._appConfig.appContext?.environment;

    this.setKinPersonId(kin_person_id);
    if (environment && this.isNotProd) {
      this.setKinSegmentId(`${environment}-${kin_person_id}`);
    }

    this.setKinId(kin_id);
  }

  public sendDataLayerEventWithQuoteData(event: string, payload: Record<string, unknown> = {}) {
    if (this.userStore.kinPersonId() == null) {
      this.pendingEvents.update((events) => [...events, { event, payload }]);
      return;
    }
    this.dispatchDataLayerEventWithQuoteData(event, payload);
  }

  private dispatchDataLayerEventWithQuoteData(event: string, payload: Record<string, unknown> = {}) {
    this.pushOnDataLayer({
      event,
      data: {
        kin_segment_user_id: this.userStore.kinSegmentId(),
        kin_id: this.userStore.kinId(),
        kin_person_id: this.userStore.kinPersonId(),
        product: HOME_PRODUCT,
        variant: 'v1', // So we know the tracking event comes from the new quoting-ui funnel
        ...this.policyStore.coverageData(),
        ...this.quoteDraftStore.quoteData(),
        ...payload,
      },
    });
  }

  public sendDataLayerUserLoginEvent() {
    if (this.segmentUserLoaded) {
      return;
    }

    // Only send the user login event if the kin_segment_user_id and kin_id are set
    if (this.userStore.kinId() !== undefined && this.userStore.kinSegmentId() !== '' && this.userStore.kinSegmentId() !== undefined) {
      this.pushOnDataLayer({
        event: SegmentEvent.USER_LOGIN,
        kin_segment_user_id: this.userStore.kinSegmentId(),
        kin_id: this.userStore.kinId(),
        kin_person_id: this.userStore.kinPersonId(),
      });

      this.segmentUserLoaded = true;
    }
  }

  private getProduct(event: string): string {
    if (event.startsWith(AUTO_EVENT_PREFIX)) {
      return AUTO_PRODUCT;
    } else if (event.startsWith(HOME_EVENT_PREFIX)) {
      return HOME_PRODUCT;
    } else {
      return '';
    }
  }

  private pushOnDataLayer(obj: Record<string, unknown>) {
    const dataLayer = this.getDataLayer() || []; // Fail gracefully when dataLayer is missing
    dataLayer.push(obj);
  }
}
